import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts'; // Horizon UI'ın kralı
import { useAuth } from 'context/AuthContext';
import { transactionsAPI, appointmentsAPI, usersAPI, bankAccountsAPI } from 'services/api';

// --- İkonlar (CoreUI yerine Material Icons) ---
import {
    MdCalendarToday, MdPersonAdd, MdSwapHoriz, MdCake,
    MdTrendingUp, MdTrendingDown, MdCreditCard, MdAdd,
    MdArrowDownward, MdPerson, MdAttachMoney, MdPieChart,
    MdChevronRight, MdMoney, MdClose
} from 'react-icons/md';

// --- Yardımcı Fonksiyonlar ---
const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
};

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
};

const translateStatus = (status) => {
    switch (status) {
        case 'approved': return 'Onaylandı';
        case 'completed': return 'Tamamlandı';
        case 'pending': return 'Bekliyor';
        case 'cancelled': return 'İptal';
        default: return status;
    }
};

const getStatusStyles = (status) => {
    switch (status) {
        case 'approved': return 'text-green-500 bg-green-100 dark:bg-green-500/20';
        case 'completed': return 'text-blue-500 bg-blue-100 dark:bg-blue-500/20';
        case 'pending': return 'text-orange-500 bg-orange-100 dark:bg-orange-500/20';
        case 'cancelled': return 'text-red-500 bg-red-100 dark:bg-red-500/20';
        default: return 'text-gray-500 bg-gray-100 dark:bg-gray-500/20';
    }
};

// --- SKELETON LOADER KOMPONENTİ ---
const DashboardSkeleton = () => (
    <div className="flex w-full flex-col gap-6 animate-pulse">
        {/* Üst Kokpit */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5 flex flex-col gap-4">
                <div className="flex gap-4"><div className="h-14 w-1/2 rounded-xl bg-gray-200 dark:bg-navy-700" /><div className="h-14 w-1/2 rounded-xl bg-gray-200 dark:bg-navy-700" /></div>
                <div className="h-14 w-full rounded-xl bg-gray-200 dark:bg-navy-700" />
                <div className="h-24 w-full rounded-xl bg-gray-200 dark:bg-navy-700" />
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (<div key={i} className="h-full min-h-[120px] rounded-xl bg-gray-200 dark:bg-navy-700" />))}
            </div>
        </div>
        {/* Aksiyon Butonları */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (<div key={i} className="h-24 rounded-xl bg-gray-200 dark:bg-navy-700" />))}
        </div>
        {/* Personel Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (<div key={i} className="h-48 rounded-xl bg-gray-200 dark:bg-navy-700" />))}
        </div>
        {/* Grafikler */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 h-[350px] rounded-xl bg-gray-200 dark:bg-navy-700" />
            <div className="h-[350px] rounded-xl bg-gray-200 dark:bg-navy-700" />
        </div>
    </div>
);

const VaultDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // --- States ---
    const [loading, setLoading] = useState(true);
    const [birthdayCount, setBirthdayCount] = useState(0);
    const [staffStatusList, setStaffStatusList] = useState([]);
    const [weeklyStats, setWeeklyStats] = useState({ income: [], expense: [], labels: [] });
    const [paymentMethodStats, setPaymentMethodStats] = useState({ labels: [], data: [] });
    const [staffPerformance, setStaffPerformance] = useState({ labels: [], received: [], completed: [] });
    const [bankAccounts, setBankAccounts] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [summaryStats, setSummaryStats] = useState({ dailyIncome: 0, dailyExpense: 0, weeklyIncome: 0, weeklyExpense: 0 });

    // Modal States
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState('income');
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        title: '', amount: '', description: '', date: new Date().toISOString().split('T')[0],
        staff_id: '', payment_method: 'cash', bank_account_id: ''
    });

    // --- Data Fetching (WORK LOGIC: BİREBİR AYNI) ---
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const today = new Date();
            const todayStr = today.toISOString().split('T')[0];

            const dayOfWeek = today.getDay() || 7;
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - dayOfWeek + 1);
            startOfWeek.setHours(0, 0, 0, 0);

            const endOfWeek = new Date(today);
            endOfWeek.setDate(today.getDate() - dayOfWeek + 7);
            endOfWeek.setHours(23, 59, 59, 999);

            const startWeekStr = startOfWeek.toISOString().split('T')[0];
            const endWeekStr = endOfWeek.toISOString().split('T')[0];

            const [staffRes, appRes, transRes, bankRes, custRes] = await Promise.all([
                usersAPI.getStaff(),
                appointmentsAPI.getAll(),
                transactionsAPI.getAll({ start_date: startWeekStr, end_date: endWeekStr }),
                bankAccountsAPI.getAll(),
                usersAPI.getCustomers()
            ]);

            const allStaff = staffRes.staff || [];
            const allAppointments = appRes.appointments || [];
            const weekTransactions = transRes.transactions || [];
            const banks = bankRes.bank_accounts || [];
            const allCustomers = custRes.customers || (Array.isArray(custRes) ? custRes : []);

            // 0. Doğum Günleri
            const todayMonthDay = todayStr.substring(5);
            const bdays = allCustomers.filter(c => {
                if (!c.birth_date) return false;
                try { return c.birth_date.substring(5, 10) === todayMonthDay; } catch(e) { return false; }
            });
            setBirthdayCount(bdays.length);
            setBankAccounts(banks);
            setStaffList(allStaff);

            // 1. Özet İstatistikler
            let dInc = 0, dExp = 0, wInc = 0, wExp = 0;
            weekTransactions.forEach(tx => {
                const amt = parseFloat(tx.amount);
                const txDate = tx.date.split('T')[0];
                if (tx.type === 'income') wInc += amt; else wExp += amt;
                if (txDate === todayStr) {
                    if (tx.type === 'income') dInc += amt; else dExp += amt;
                }
            });
            setSummaryStats({ dailyIncome: dInc, dailyExpense: dExp, weeklyIncome: wInc, weeklyExpense: wExp });

            // 2. Canlı Personel Durumu
            const nowTimeStr = new Date().toTimeString().substring(0, 5);
            const todayApps = allAppointments.filter(a => a.appointment_date.startsWith(todayStr) && a.status !== 'cancelled');

            const staffStatusData = allStaff.filter(s => s.is_active).map(staff => {
                const myApps = todayApps.filter(a => a.staff_id === staff.id);
                const currentApp = myApps
                    .filter(a => a.appointment_time <= nowTimeStr && a.status !== 'completed')
                    .sort((a, b) => b.appointment_time.localeCompare(a.appointment_time))[0];
                const nextApp = myApps
                    .filter(a => a.appointment_time > nowTimeStr)
                    .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time))[0];

                const totalToday = myApps.length;
                const completedToday = myApps.filter(a => a.status === 'completed').length;
                const progress = totalToday > 0 ? (completedToday / totalToday) * 100 : 0;
                return { ...staff, currentApp, nextApp, progress, totalToday, completedToday };
            });
            setStaffStatusList(staffStatusData);

            // 3. Grafikler (ApexCharts formatına hazırlık)
            const days = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
            const incomeData = Array(7).fill(0);
            const expenseData = Array(7).fill(0);
            weekTransactions.forEach(tx => {
                const txDate = new Date(tx.date);
                let dayIndex = txDate.getDay() - 1;
                if (dayIndex === -1) dayIndex = 6;
                if (tx.type === 'income') incomeData[dayIndex] += parseFloat(tx.amount);
                else expenseData[dayIndex] += parseFloat(tx.amount);
            });
            setWeeklyStats({ labels: days, income: incomeData, expense: expenseData });

            const methodMap = { 'Nakit': 0 };
            banks.forEach(b => methodMap[b.name] = 0);
            weekTransactions.filter(t => t.type === 'income').forEach(tx => {
                if (tx.payment_method === 'cash') {
                    methodMap['Nakit'] += parseFloat(tx.amount);
                } else if (tx.bank_account_id) {
                    const bankName = banks.find(b => b.id === tx.bank_account_id)?.name || 'Diğer';
                    methodMap[bankName] = (methodMap[bankName] || 0) + parseFloat(tx.amount);
                }
            });
            setPaymentMethodStats({ labels: Object.keys(methodMap), data: Object.values(methodMap) });

            const weekApps = allAppointments.filter(a => a.appointment_date >= startWeekStr && a.appointment_date <= endWeekStr);
            const staffNames = [], receivedCounts = [], completedCounts = [];
            allStaff.filter(s => s.is_active).forEach(staff => {
                staffNames.push(staff.name);
                const myWeekApps = weekApps.filter(a => a.staff_id === staff.id);
                receivedCounts.push(myWeekApps.length);
                completedCounts.push(myWeekApps.filter(a => a.status === 'completed').length);
            });
            setStaffPerformance({ labels: staffNames, received: receivedCounts, completed: completedCounts });

        } catch (err) {
            console.error("Dashboard yüklenemedi:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

    // --- Handlers ---
    const handleOpenModal = (type) => {
        setModalType(type);
        setFormData({ title: '', amount: '', description: '', date: new Date().toISOString().split('T')[0], staff_id: '', payment_method: 'cash', bank_account_id: '' });
        setModalVisible(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.amount) return;
        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                type: modalType,
                category: modalType === 'income' ? 'manual_income' : 'manual_expense',
                bank_account_id: formData.payment_method === 'cash' ? null : formData.bank_account_id,
                staff_id: formData.staff_id || null
            };
            await transactionsAPI.create(payload);
            setModalVisible(false);
            fetchDashboardData();
        } catch (err) {
            alert("Kaydedilemedi: " + (err.response?.data?.message || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="mt-5"><DashboardSkeleton /></div>;

    // --- APEXCHARTS AYARLARI (Horizon UI Standartları) ---
    const areaChartOptions = {
        chart: { toolbar: { show: false }, dropShadow: { enabled: true, top: 13, left: 0, blur: 10, opacity: 0.1, color: "#4318FF" } },
        colors: ["#22c55e", "#ef4444"],
        markers: { size: 0 },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        xaxis: { type: "category", categories: weeklyStats.labels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#A3AED0", fontSize: "12px", fontWeight: "500" } } },
        yaxis: { show: false },
        legend: { show: false },
        grid: { show: false },
        tooltip: { theme: "dark" }
    };
    const barChartOptionsHorizontal = {
        chart: { toolbar: { show: false } },
        colors: ["#4318FF", "#F59E0B", "#EF4444", "#10B981", "#3B82F6"],
        plotOptions: { bar: { borderRadius: 4, horizontal: true, columnWidth: "20px", distributed: true } },
        dataLabels: { enabled: false },
        xaxis: { categories: paymentMethodStats.labels, labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { style: { colors: "#A3AED0", fontSize: "14px", fontWeight: "500" } } },
        grid: { show: false },
        legend: { show: false },
        tooltip: { theme: "dark" }
    };
    const barChartOptionsVertical = {
        chart: { toolbar: { show: false }, stacked: false },
        colors: ["#4318FF", "#10B981"],
        plotOptions: { bar: { borderRadius: 4, columnWidth: "40%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: staffPerformance.labels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#A3AED0", fontSize: "12px", fontWeight: "500" } } },
        yaxis: { show: false },
        grid: { show: false },
        legend: { position: 'top', horizontalAlign: 'right', labels: { colors: "#A3AED0" } },
        tooltip: { theme: "dark" }
    };

    return (
        <div className="mt-5 flex w-full flex-col gap-6">

            {/* ========================================
          0. ÜST KOKPİT (Özet ve Yönlendirmeler)
          ======================================== */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">

                {/* SOL: Hızlı Yönlendirmeler */}
                <div className="flex flex-col gap-4 lg:col-span-5">
                    <div className="flex gap-4">
                        <button onClick={() => navigate('/admin/calendar')} className="linear flex flex-1 items-center justify-center gap-2 rounded-[20px] border border-gray-200 bg-white p-4 font-bold text-navy-700 shadow-sm transition-all hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700">
                            <MdCalendarToday className="text-xl text-brand-500" /> Takvim
                        </button>
                        <button onClick={() => navigate('/vault/newcustomer')} className="linear flex flex-1 items-center justify-center gap-2 rounded-[20px] border border-gray-200 bg-white p-4 font-bold text-navy-700 shadow-sm transition-all hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700">
                            <MdPersonAdd className="text-xl text-amber-500" /> Yeni Müşteri
                        </button>
                    </div>

                    <button onClick={() => navigate('/vault/transaction')} className="linear flex w-full items-center justify-center gap-2 rounded-[20px] border border-dashed border-gray-300 bg-transparent p-4 text-sm font-medium text-gray-600 transition-all hover:bg-gray-50 dark:border-white/20 dark:text-gray-300 dark:hover:bg-navy-800">
                        <MdSwapHoriz className="text-lg" /> Tüm Finansal İşlemlere Git
                    </button>

                    {/* DOĞUM GÜNÜ KARTI (Premium Gradient) */}
                    <button
                        onClick={() => navigate('/admin/customer')}
                        className={`linear relative overflow-hidden rounded-[20px] p-4 text-left shadow-lg transition-all hover:-translate-y-1 ${
                            birthdayCount > 0
                                ? 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-orange-500/30'
                                : 'bg-white shadow-shadow-500 dark:bg-navy-800 dark:shadow-none'
                        }`}
                    >
                        <div className="relative z-10 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-full ${birthdayCount > 0 ? 'bg-white/20 text-white' : 'bg-gray-100 text-amber-500 dark:bg-navy-700'}`}>
                                    <MdCake className="text-2xl" />
                                </div>
                                <div>
                                    <h4 className={`text-base font-bold ${birthdayCount > 0 ? 'text-white' : 'text-navy-700 dark:text-white'}`}>Doğum Günleri</h4>
                                    <p className={`text-sm ${birthdayCount > 0 ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                                        {birthdayCount > 0 ? `Bugün ${birthdayCount} müşterinin doğum günü! 🥳` : 'Bugün doğum günü olan müşteri yok.'}
                                    </p>
                                </div>
                            </div>
                            <MdChevronRight className={`text-2xl ${birthdayCount > 0 ? 'text-white/50' : 'text-gray-400'}`} />
                        </div>
                    </button>
                </div>

                {/* SAĞ: Finansal Özet Kartları */}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:col-span-7">
                    <div className="flex flex-col items-center justify-center rounded-[20px] bg-white p-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
                        <span className="text-xs font-bold uppercase text-gray-400">Günlük Gelir</span>
                        <span className="mt-2 text-xl font-bold text-green-500">{formatCurrency(summaryStats.dailyIncome)}</span>
                        <MdTrendingUp className="mt-1 text-green-500/50" />
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-[20px] bg-white p-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
                        <span className="text-xs font-bold uppercase text-gray-400">Günlük Gider</span>
                        <span className="mt-2 text-xl font-bold text-red-500">{formatCurrency(summaryStats.dailyExpense)}</span>
                        <MdTrendingDown className="mt-1 text-red-500/50" />
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-[20px] bg-brand-50/50 p-4 dark:bg-brand-400/10">
                        <span className="text-xs font-bold uppercase text-brand-500 dark:text-brand-400">Haftalık Gelir</span>
                        <span className="mt-2 text-xl font-bold text-navy-700 dark:text-white">{formatCurrency(summaryStats.weeklyIncome)}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-[20px] bg-red-50/50 p-4 dark:bg-red-400/10">
                        <span className="text-xs font-bold uppercase text-red-500 dark:text-red-400">Haftalık Gider</span>
                        <span className="mt-2 text-xl font-bold text-navy-700 dark:text-white">{formatCurrency(summaryStats.weeklyExpense)}</span>
                    </div>
                </div>
            </div>

            {/* ========================================
          1. ANA İŞLEM BUTONLARI
          ======================================== */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <button onClick={() => navigate('/vault/adisyon')} className="group flex items-center justify-center gap-4 rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 p-6 shadow-lg shadow-brand-500/30 transition-all hover:-translate-y-1">
                    <MdCreditCard className="text-4xl text-white opacity-80 transition-transform group-hover:scale-110" />
                    <div className="text-left text-white">
                        <h4 className="text-xl font-bold">Adisyon Oluştur</h4>
                        <p className="text-sm font-medium opacity-80">Yeni satış kaydı</p>
                    </div>
                </button>
                <button onClick={() => handleOpenModal('income')} className="group flex items-center justify-center gap-4 rounded-[20px] bg-gradient-to-br from-green-400 to-green-600 p-6 shadow-lg shadow-green-500/30 transition-all hover:-translate-y-1">
                    <MdAdd className="text-4xl text-white opacity-80 transition-transform group-hover:scale-110" />
                    <div className="text-left text-white">
                        <h4 className="text-xl font-bold">Gelir Ekle</h4>
                        <p className="text-sm font-medium opacity-80">Manuel tahsilat</p>
                    </div>
                </button>
                <button onClick={() => handleOpenModal('expense')} className="group flex items-center justify-center gap-4 rounded-[20px] bg-gradient-to-br from-red-400 to-red-600 p-6 shadow-lg shadow-red-500/30 transition-all hover:-translate-y-1">
                    <MdArrowDownward className="text-4xl text-white opacity-80 transition-transform group-hover:scale-110" />
                    <div className="text-left text-white">
                        <h4 className="text-xl font-bold">Gider Ekle</h4>
                        <p className="text-sm font-medium opacity-80">Harcama kaydı</p>
                    </div>
                </button>
            </div>

            {/* ========================================
          2. CANLI PERSONEL DURUMU
          ======================================== */}
            <div>
                <div className="mb-4 flex items-center justify-between">
                    <h4 className="flex items-center text-lg font-bold text-navy-700 dark:text-white">
                        <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-white"><MdPerson /></div>
                        Personel Takibi
                    </h4>
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-bold text-navy-700 dark:bg-navy-700 dark:text-white">
            Aktif: {staffStatusList.length}
          </span>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {staffStatusList.map(staff => (
                        <div key={staff.id} className="flex flex-col overflow-hidden rounded-[20px] bg-white bg-clip-border shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">

                            {/* Header */}
                            <div className="flex items-center border-b border-gray-100 p-4 dark:border-white/10">
                                <div className="relative">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-lg font-bold text-white shadow-md">
                                        {staff.profile_picture ? <img src={staff.profile_picture} className="h-full w-full rounded-full object-cover" alt="avatar" /> : getInitials(staff.name)}
                                    </div>
                                    <div className={`absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white dark:border-navy-800 ${staff.currentApp ? 'bg-green-500' : 'bg-gray-400'}`} />
                                </div>
                                <div className="ml-3 flex-1">
                                    <h5 className="text-base font-bold text-navy-700 dark:text-white">{staff.name} {staff.surname}</h5>
                                    <p className="text-xs font-medium text-gray-500">{staff.tag || 'Personel'}</p>
                                </div>
                                <div className="w-16 text-right">
                                    <p className="mb-1 text-xs font-medium text-gray-500">Doluluk</p>
                                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
                                        <div className="h-full bg-brand-500" style={{ width: `${staff.progress}%` }} />
                                    </div>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="flex flex-col p-4">
                                {/* Şu An */}
                                <div className={`mb-3 rounded-xl p-3 transition-colors ${staff.currentApp ? 'bg-green-50/50 dark:bg-green-500/10 border border-green-100 dark:border-green-500/20' : 'bg-gray-50 dark:bg-navy-700/50'}`}>
                                    <div className="mb-2 flex items-center justify-between">
                                        <div className="flex items-center text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                            <div className="mr-1.5 h-2 w-2 rounded-full bg-current" /> ŞU AN
                                        </div>
                                        {staff.currentApp && (
                                            <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${getStatusStyles(staff.currentApp.status)}`}>
                        {translateStatus(staff.currentApp.status)}
                      </span>
                                        )}
                                    </div>

                                    {staff.currentApp ? (
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h6 className="text-sm font-bold text-navy-700 dark:text-white">{staff.currentApp.customer?.name} {staff.currentApp.customer?.surname}</h6>
                                                <p className="text-xs font-medium text-gray-500">{staff.currentApp.service?.name}</p>
                                            </div>
                                            <div className="text-right">
                                                <span className="font-mono text-lg font-bold text-navy-700 dark:text-white">{staff.currentApp.appointment_time.substring(0,5)}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="py-2 text-center text-xs font-medium italic text-gray-400">Şu an işlem yok (Boşta)</p>
                                    )}
                                </div>

                                {/* Sıradaki */}
                                <div className="flex items-center px-1">
                                    <MdChevronRight className="mr-2 text-gray-400" />
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold uppercase text-gray-400">SIRADAKİ MÜŞTERİ</p>
                                        {staff.nextApp ? (
                                            <div className="flex items-end justify-between">
                                                <span className="text-sm font-semibold text-navy-700 dark:text-gray-300">{staff.nextApp.customer?.name} {staff.nextApp.customer?.surname}</span>
                                                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs font-bold text-gray-600 dark:bg-navy-700 dark:text-gray-400">{staff.nextApp.appointment_time.substring(0,5)}</span>
                                            </div>
                                        ) : (
                                            <span className="text-xs font-medium italic text-gray-400">Planlanmış randevu yok</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ========================================
          3. GRAFİKLER (APEXCHARTS)
          ======================================== */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {/* Gelir Akışı (Area) */}
                <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-6 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none lg:col-span-2">
                    <div className="mb-2 flex items-center justify-between">
                        <h5 className="flex items-center text-lg font-bold text-navy-700 dark:text-white">
                            <MdAttachMoney className="mr-2 text-green-500" /> Haftalık Gelir Akışı
                        </h5>
                    </div>
                    <div className="h-[300px] w-full">
                        <Chart options={areaChartOptions} series={[{name: 'Gelir', data: weeklyStats.income}, {name: 'Gider', data: weeklyStats.expense}]} type="area" width="100%" height="100%" />
                    </div>
                </div>

                {/* Tahsilat Dağılımı (Bar) */}
                <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-6 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
                    <div className="mb-2 flex items-center justify-between">
                        <h5 className="flex items-center text-lg font-bold text-navy-700 dark:text-white">
                            <MdPieChart className="mr-2 text-brand-500" /> Tahsilat Dağılımı
                        </h5>
                    </div>
                    <div className="h-[300px] w-full">
                        <Chart options={barChartOptionsHorizontal} series={[{name: 'Tutar', data: paymentMethodStats.data}]} type="bar" width="100%" height="100%" />
                    </div>
                </div>

                {/* Personel Performansı */}
                <div className="flex flex-col rounded-[20px] bg-white bg-clip-border p-6 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none lg:col-span-3">
                    <div className="mb-2 flex items-center justify-between">
                        <h5 className="flex items-center text-lg font-bold text-navy-700 dark:text-white">
                            <MdPerson className="mr-2 text-amber-500" /> Personel Performansı
                        </h5>
                    </div>
                    <div className="h-[300px] w-full">
                        <Chart options={barChartOptionsVertical} series={[{name: 'Alınan Randevu', data: staffPerformance.received}, {name: 'Tamamlanan', data: staffPerformance.completed}]} type="bar" width="100%" height="100%" />
                    </div>
                </div>
            </div>

            {/* ========================================
          MANUEL İŞLEM MODALI (TAILWIND NATIVE)
          ======================================== */}
            {modalVisible && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm transition-all">
                    <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800 sm:p-8">
                        <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-4 dark:border-white/10">
                            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                                {modalType === 'income' ? 'Gelir Ekle' : 'Gider Ekle'}
                            </h3>
                            <button onClick={() => setModalVisible(false)} className="rounded-full p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700">
                                <MdClose className="h-6 w-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            {/* Başlık */}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">Başlık *</label>
                                <input required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder={modalType === 'income' ? 'Örn: Elden Satış' : 'Örn: Temizlik Malzemesi'} className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400" />
                            </div>

                            {/* Tutar & Tarih */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">Tutar (TL) *</label>
                                    <input required type="number" step="0.01" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400" />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">Tarih *</label>
                                    <input required type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400" />
                                </div>
                            </div>

                            {/* Ödeme Yöntemi */}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">Ödeme Yöntemi</label>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setFormData({...formData, payment_method: 'cash', bank_account_id: ''})} className={`flex flex-1 items-center justify-center gap-2 rounded-xl border p-3 text-sm font-medium transition-all ${formData.payment_method === 'cash' ? 'border-brand-500 bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400' : 'border-gray-200 text-gray-600 dark:border-white/10 dark:text-gray-400'}`}>
                                        <MdMoney className="text-lg" /> Nakit
                                    </button>
                                    <button type="button" onClick={() => setFormData({...formData, payment_method: 'card'})} className={`flex flex-1 items-center justify-center gap-2 rounded-xl border p-3 text-sm font-medium transition-all ${formData.payment_method === 'card' ? 'border-brand-500 bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400' : 'border-gray-200 text-gray-600 dark:border-white/10 dark:text-gray-400'}`}>
                                        <MdCreditCard className="text-lg" /> Kart / Havale
                                    </button>
                                </div>
                            </div>

                            {/* Banka Hesabı (Şartlı) */}
                            {['card', 'transfer'].includes(formData.payment_method) && (
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">Banka Hesabı</label>
                                    <select required value={formData.bank_account_id} onChange={e => setFormData({...formData, bank_account_id: e.target.value})} className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:bg-navy-800">
                                        <option value="">Seçiniz...</option>
                                        {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>
                            )}

                            {/* Personel (Opsiyonel) */}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">İlgili Personel (Opsiyonel)</label>
                                <select value={formData.staff_id} onChange={e => setFormData({...formData, staff_id: e.target.value})} className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:bg-navy-800">
                                    <option value="">Yok</option>
                                    {staffList.map(s => <option key={s.id} value={s.id}>{s.name} {s.surname}</option>)}
                                </select>
                            </div>

                            {/* Açıklama */}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">Açıklama</label>
                                <textarea rows="2" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400" />
                            </div>

                            {/* Modal Aksiyonları */}
                            <div className="mt-4 flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-white/10">
                                <button type="button" onClick={() => setModalVisible(false)} disabled={submitting} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700">İptal</button>
                                <button type="submit" disabled={submitting} className={`flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-medium text-white transition-all ${modalType === 'income' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'} disabled:cursor-not-allowed disabled:opacity-70`}>
                                    {submitting ? <MdClose className="h-5 w-5 animate-spin" /> : 'Kaydet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VaultDashboard;